import React, { useEffect, useState, } from 'react';
import { IntlProvider, useIntl } from 'react-intl';
import { Button } from 'antd';
import { ArrowsClockwiseIcon } from '@phosphor-icons/react';
import { detectBrowserlanguage } from './services/language';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import ErrorBoundary from './components/error-boundary';
import { WakeUp, } from './services/utils';
import Routers from './routes';
import NotificationBridge from './services/notification/notification.bridge';
import RefreshRootMsg from './refresh.root.msg';

import messages_nl from "./translations/nl.json";
import messages_en from "./translations/en.json";
// import messages_de from "./translations/de.json";


const AppRoot = () => {

  const [currentLanguage, setCurrentLanguage] = useState(detectBrowserlanguage());
  const [newVersion, setNewVersion] = useState(false);

  const messages = {
    nl: messages_nl,
    en: messages_en,
    // de: messages_de,
  };

  // little call to wake up the server, so the first request is not so slow.
  useEffect(() => {
    WakeUp().then()
      .catch(error => {
        console.error('error: ', error);
      });
  }, []);

  // Register service worker once on mount (not on every render).
  useEffect(() => {
    serviceWorkerRegistration.register({
      onUpdate: registration => {
        const waitingServiceWorker = registration.waiting;

        if (waitingServiceWorker) {
          waitingServiceWorker.addEventListener("statechange", event => {
            if (event.target.state === "activated") {
              setNewVersion(true);
            }
          });
          waitingServiceWorker.postMessage({ type: "SKIP_WAITING" });
        }
      },
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <IntlProvider locale={currentLanguage} messages={messages[currentLanguage]}>
      <ErrorBoundary fallback={<AppCrashFallback />}>
        <NotificationBridge />
        <Routers
          locale={currentLanguage}
          setCurrentLanguage={setCurrentLanguage}
        />
        <RefreshRootMsg isNotificationOpen={newVersion} />
      </ErrorBoundary>
    </IntlProvider>
  );
}

export default AppRoot;

/**
 * Translated error fallback rendered by ErrorBoundary when the app crashes.
 * Lives inside IntlProvider so it can use useIntl.
 * onRefresh, error, and componentStack are injected by ErrorBoundary via React.cloneElement.
 */
const AppCrashFallback = ({ onRefresh, error, componentStack }) => {
  const intl = useIntl();
  const [copied, setCopied] = React.useState(false);

  const errorText = error ? [
    `${error.name}: ${error.message}`,
    '',
    error.stack || '(no stack trace)',
    componentStack ? `\nComponent Stack:${componentStack}` : '',
  ].join('\n').trim() : '';

  const handleCopy = () => {
    if (navigator.clipboard && errorText) {
      navigator.clipboard.writeText(errorText)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2500);
        })
        .catch(() => {});
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <div style={{ textAlign: 'center', padding: '0 24px', maxWidth: 640, width: '100%' }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>⚠️</div>
        <h2>{intl.formatMessage({ id: 'app-crashed' })}</h2>
        <p style={{ color: 'rgba(0,0,0,0.45)', marginBottom: 16 }}>
          {intl.formatMessage({ id: 'app-crashed-info' })}
        </p>

        {error && (
          <div style={{ textAlign: 'left', marginBottom: 16 }}>
            <div style={{
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: 4,
              padding: '8px 12px',
              fontSize: 13,
              wordBreak: 'break-word',
            }}>
              <strong>{error.name}</strong>: {error.message}
            </div>
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: 'pointer', color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>
                {intl.formatMessage({ id: 'app-crashed-stack-label' })}
              </summary>
              <pre style={{
                fontSize: 11,
                background: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: 4,
                padding: 8,
                overflow: 'auto',
                maxHeight: 200,
                marginTop: 6,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}>
                {errorText}
              </pre>
            </details>
            <Button
              size="small"
              style={{ marginTop: 8, width: '100%' }}
              onClick={handleCopy}
            >
              {copied
                ? intl.formatMessage({ id: 'app-crashed-copied' })
                : intl.formatMessage({ id: 'app-crashed-copy' })}
            </Button>
          </div>
        )}

        <Button
          type="primary"
          size="large"
          icon={<ArrowsClockwiseIcon size={16} style={{ marginRight: 6 }} />}
          onClick={onRefresh}
        >
          {intl.formatMessage({ id: 'app-crashed-refresh' })}
        </Button>
      </div>
    </div>
  );
};
